import { useState, useEffect, useMemo } from "react";
import * as Icon from "react-feather";
import { Link, useParams, useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import APIManager from "../../managers/APIManager";
import HelperVideo from "../../helpers/video";

import Button from "../common/Button";
import Card from "../common/Card";
import Form from "../common/Form";
import Loading from "../common/Loading";
import Page from "../common/Page";
import Modal from "../common/Modal";

import thumbnailPlaceholder from "../../assets/images/thumbnail-placeholder.png";
import { VideoActionCreators } from "../../store/actions/video";

const VideoEdit = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const videoId = useMemo(() => params.video, [params.video]);
  const [player, setPlayer] = useState(null);
  const [video, setVideo] = useState(null);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const dispatch = useDispatch();
  const videoInfrastructure = useSelector(
    (state) => state?.auth?.user?.videoInfrastructure,
  );
  const integrationType = useSelector(
    (state) => state?.auth?.user?.integrationType,
  );

  useEffect(() => {
    APIManager.getVideo(videoId).then((res) => {
      if (res.data.status === "DELETED") {
        navigate("/videos");
      }

      if (res.data.id.startsWith("nkt:")) {
        res.data.source = `https://vm.empower.net/embed/npm_empowernet/${res.data.id.substring(
          4,
        )}`;
      }

      setVideo(res.data);
    });
  }, []);

  useEffect(() => {
    if (
      !video ||
      player ||
      video.status !== "AVAILABLE" ||
      video.id.startsWith("nkt:")
    ) {
      return;
    }
    const playerVar = new window.EmpowerPlayer("preview", {
      adTimeout: 1,
      language: "en",
      logo: { image: "" },
      playerName: "empower_console",
    });
    playerVar.setInfoLoadURL(
      `${process.env.REACT_APP_API_BASE}/videos/{VIDEO_ID}/playback`,
    );
    playerVar.loadVideo(videoId);
    setPlayer(playerVar);
  }, [navigate, videoId, video, player, videoId]);

  const deleteVideo = (e) => {
    e.preventDefault();
    const confirmation = window.confirm(
      "Are you sure you want to delete this video? This operation is irreversible.",
    );
    if (!confirmation) {
      return;
    }
    if (typeof videoId !== "undefined") {
      if (videoInfrastructure === null || videoInfrastructure === "AWS") {
        APIManager.deleteVideo(videoId).then(() => {
          navigate("/videos");
        });
      } else {
        APIManager.izleseneDeleteVideo(videoId).then(() => {
          navigate("/videos");
        });
      }
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setIsConfirmModalVisible(true);
  };

  const handleConfirmSave = async () => {
    try {
      setIsSaving(true);

      if (videoInfrastructure === null || videoInfrastructure === "AWS") {
        await dispatch(VideoActionCreators.updateVideo(video));
      } else {
        await dispatch(VideoActionCreators.izleseneUpdateVideo(video));
      }

      await dispatch(VideoActionCreators.getVideos());

      toast.success(t("your video updated successfully"));

      navigate("/videos");
    } catch (err) {
      console.error("Error updating video:", err);
      toast.error(t("error saving video creation settings"));
    } finally {
      setIsSaving(false);
      setIsConfirmModalVisible(false);
    }
  };

  const onChange = (e) => {
    e.preventDefault();
    setVideo((prevVideo) => ({
      ...prevVideo,
      [e.target.name]: e.target.value,
    }));
  };

  if (video === null) {
    return <Loading />;
  }

  return (
    <Page
      title={video.title}
      controls={
        <>
          {/* <Button
            className="mr-2"
            RootComponent={Link}
            to={`/analytics/videos/${video.id}`}
            color="primary"
          >
            <Icon.BarChart2
              className="inline mr-1 text-empower-500"
              width="16"
              height="16"
            />
            Analytics
          </Button> */}

          {integrationType !== -1 && videoInfrastructure !== "IZLESENE" && (
            <Button
              RootComponent={Link}
              to={`/videos/${video.id}/embed`}
              color="primary"
            >
              <Icon.Code
                className="inline text-empower-500 mr-1"
                width="16"
                height="16"
              />
              Get Embed Code
            </Button>
          )}
        </>
      }
    >
      {HelperVideo.getVideoStatusAlert(video)}
      <div className="flex flex-col md:flex-row">
        <div className="flex-1 w-full md:w-1/2 mr-5">
          <Card title="Edit Video">
            <Card.Body>
              <Form.FormTextInput
                name="title"
                label="Title"
                onChange={onChange}
                value={video && video.title}
              />
              <Form.Textarea
                name="description"
                label="Description"
                onChange={onChange}
                value={video && video.description}
              />
              <Form.FormTextInput
                readOnly={true}
                label="Source"
                onChange={onChange}
                value={video && video.source}
              />
              <Form.Label>Thumbnail</Form.Label>
              {video.status === "AVAILABLE" ? (
                <img
                  src={video.thumbnail}
                  onError={(e) => {
                    e.target.src = thumbnailPlaceholder;
                  }}
                  alt="Video Thumbnail"
                  width="240"
                  height="135"
                />
              ) : (
                <img
                  src={thumbnailPlaceholder}
                  alt="Video Thumbnail"
                  width="240"
                  height="135"
                />
              )}
            </Card.Body>
            <Card.Footer className="flex justify-between">
              <Button
                className="mr-1"
                color="danger"
                data-video={video.id}
                onClick={deleteVideo}
              >
                <Icon.Trash2 className="inline mr-1" width="16" height="16" />
                Delete
              </Button>
              <Button color="primary" onClick={onSubmit}>
                <Icon.Check
                  className="inline mr-1 text-empower-500"
                  width="16"
                  height="16"
                />
                Save Changes
              </Button>
            </Card.Footer>
          </Card>
        </div>
        <div className="w-auto">
          <Card className="p-2">
            {video.id.startsWith("nkt:") ? (
              <iframe
                title="Video Player"
                src={
                  video?.source
                    ? `${video?.source}${video?.source?.includes("?") ? "&" : "?"}noads=1`
                    : ""
                }
                width="498"
                height="280"
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            ) : (
              <div
                id="preview"
                className="empower-player"
                data-empower-id={video.id}
                data-empower-options='{"language":"en"}'
                style={{
                  backgroundColor: "#000",
                  height: 280,
                  width: 498,
                }}
              />
            )}
          </Card>
        </div>
      </div>

      <Modal
        isVisible={isConfirmModalVisible}
        onClose={() => {
          if (!isSaving) {
            setIsConfirmModalVisible(false);
          }
        }}
        title="confirm changes"
      >
        <div className="px-6 pb-4 w-full">
          <p className="text-gray-700 mb-4">
            {t("are you sure you want to save the changes?")}
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              color="secondary"
              disabled={isSaving}
              onClick={() => setIsConfirmModalVisible(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isSaving}
              onClick={handleConfirmSave}
            >
              <Icon.Check
                className="inline mr-1 text-empower-500"
                width="16"
                height="16"
              />
              {isSaving ? "Saving..." : "Confirm"}
            </Button>
          </div>
        </div>
      </Modal>
    </Page>
  );
};

export default VideoEdit;
